import React, { useState, useEffect } from 'react';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Clock,
  RefreshCw
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer 
} from 'recharts';

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [volumeTrends, setVolumeTrends] = useState([]);
  const [kycDistribution, setKycDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchDashboardData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      // Fetch all data in parallel
      const [metricsRes, trendsRes, kycRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/dashboard/metrics`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/dashboard/volume-trends`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/dashboard/kyc-distribution`, { headers })
      ]);

      const metricsData = await metricsRes.json();
      const trendsData = await trendsRes.json();
      const kycData = await kycRes.json();

      setMetrics(metricsData);
      setVolumeTrends(trendsData.trends || []);
      setKycDistribution(kycData.distribution || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const KPICard = ({ title, value, icon: Icon, color, change }) => (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6 hover:border-cyan-400/50 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {change && (
          <span className={`text-sm font-semibold ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {change > 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <h3 className="text-cyan-600 text-sm font-semibold mb-1">{title}</h3>
      <p className="text-white text-3xl font-bold">{value?.toLocaleString() || '0'}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-400 font-semibold">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Dashboard Overview</h1>
          <p className="text-cyan-400">Real-time platform statistics and analytics</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Last Update */}
      <div className="text-right text-cyan-600 text-sm">
        Last updated: {lastUpdate.toLocaleTimeString()}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Users"
          value={metrics?.total_users}
          icon={Users}
          color="from-cyan-500 to-cyan-600"
        />
        <KPICard
          title="Total Trades"
          value={metrics?.total_trades}
          icon={TrendingUp}
          color="from-green-500 to-green-600"
        />
        <KPICard
          title="Total Volume (USD)"
          value={`$${metrics?.total_volume_usd?.toLocaleString()}`}
          icon={DollarSign}
          color="from-blue-500 to-blue-600"
        />
        <KPICard
          title="Pending KYC"
          value={metrics?.pending_kyc_requests}
          icon={Clock}
          color="from-orange-500 to-orange-600"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Volume Trends Chart */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            7-Day Volume Trends
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={volumeTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis 
                dataKey="date" 
                stroke="#22d3ee" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#22d3ee" 
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #22d3ee',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                formatter={(value) => [`$${value.toLocaleString()}`, 'Volume']}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="volume" 
                stroke="#22d3ee" 
                strokeWidth={3}
                dot={{ fill: '#22d3ee', r: 5 }}
                activeDot={{ r: 7 }}
                name="Trading Volume"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* KYC Distribution Chart */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            KYC Status Distribution
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={kycDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {kycDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #22d3ee',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Legend */}
          <div className="mt-4 grid grid-cols-2 gap-2">
            {kycDistribution.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm text-cyan-400">
                  {item.name}: <span className="font-semibold text-white">{item.value}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Quick Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-slate-800/50 border border-cyan-500/20">
            <p className="text-cyan-600 text-sm mb-1">Active Users (7 days)</p>
            <p className="text-white text-2xl font-bold">{metrics?.active_users_7d || 0}</p>
          </div>
          <div className="p-4 rounded-lg bg-slate-800/50 border border-cyan-500/20">
            <p className="text-cyan-600 text-sm mb-1">Average Trade Volume</p>
            <p className="text-white text-2xl font-bold">
              ${((metrics?.total_volume_usd || 0) / (metrics?.total_trades || 1)).toFixed(2)}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-slate-800/50 border border-cyan-500/20">
            <p className="text-cyan-600 text-sm mb-1">KYC Approval Rate</p>
            <p className="text-white text-2xl font-bold">
              {kycDistribution.length > 0 
                ? ((kycDistribution.find(k => k.name === 'Verified')?.value || 0) / 
                   kycDistribution.reduce((sum, k) => sum + k.value, 0) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
